import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonLabel,
  IonPage, IonSegment, IonSegmentButton, IonSpinner, IonTitle, IonToolbar,
} from '@ionic/react';
import { calendarOutline, chevronDownOutline, refreshOutline } from 'ionicons/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { EmptyHint, palette } from '../../components/design.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useOperationalIdentity } from '../../lib/auth/useOperationalIdentity.js';
import { useEvents, useMasters } from '../../lib/data/queries.js';
import { groupEvidenceByDay, localDayKey } from '../../lib/haccp/operationalEvidenceArchive.js';
import {
  listLocalOperationalEvidencePreviews,
  makeOperationalEvidenceRetryable,
} from '../../lib/haccp/operationalEvidenceOutbox.js';
import { getSupabase } from '../../lib/supabase.js';
import { getSyncEngine } from '../../lib/sync/engine.js';

interface EvidencePhoto { storage_path: string; captured_at: string }
interface EvidenceEvent {
  id: string;
  client_ts: string;
  kind: 'cleaning_done' | 'temperature_reading';
  payload: {
    schedule_id?: string;
    storage_unit_id?: string;
    temperature_c?: number;
    operator_name?: string | null;
    evidence_photo?: EvidencePhoto;
    _operator?: { full_name?: string };
    _operational?: { tenant_personnel_id?: string };
  };
}
interface NamedRow { id: string; name?: string; title?: string }
interface PhotoLink { url?: string; error?: string }

function dayLabel(day: string): string {
  const today = localDayKey(new Date().toISOString());
  const yesterday = localDayKey(new Date(Date.now() - 86_400_000).toISOString());
  const formatted = new Date(`${day}T12:00:00`).toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  if (day === today) return `Oggi · ${formatted}`;
  if (day === yesterday) return `Ieri · ${formatted}`;
  return formatted;
}

export function OperationalEvidencePage(): JSX.Element {
  const identity = useOperationalIdentity();
  const { profile } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialKind = initialParams.get('kind');
  const [filter, setFilter] = useState<'all' | 'cleaning' | 'temperature'>(
    initialKind === 'cleaning' || initialKind === 'temperature' ? initialKind : 'all',
  );
  const [selectedDay, setSelectedDay] = useState(initialParams.get('date') ?? '');
  const [openDays, setOpenDays] = useState<Set<string> | null>(null);
  const [retrying, setRetrying] = useState(false);
  const targetFilter = initialParams.get('target');
  const { data: cleaningCorrections = [] } = useEvents('cleaning_voided', null);
  const cancelledCleanings = new Set(cleaningCorrections.flatMap((event) => Array.isArray(event.payload.original_client_uuids) ? event.payload.original_client_uuids as string[] : []));
  const { data: cleanings = [] } = useEvents('cleaning_done', null, true);
  const { data: temperatures = [] } = useEvents('temperature_reading', null);
  const { data: schedules = [] } = useMasters<NamedRow>('cleaning_schedules');
  const { data: units = [] } = useMasters<NamedRow>('storage_units');

  const evidence = useMemo(() => ([...cleanings, ...temperatures] as EvidenceEvent[])
    .filter((event) => !!event.payload.evidence_photo)
    .filter((event) => filter === 'all' || (filter === 'cleaning' ? event.kind === 'cleaning_done' : event.kind === 'temperature_reading'))
    .filter((event) => !targetFilter || event.payload.schedule_id === targetFilter || event.payload.storage_unit_id === targetFilter)
    .filter((event) => !selectedDay || localDayKey(event.client_ts) === selectedDay)
    .sort((a, b) => b.client_ts.localeCompare(a.client_ts)), [cleanings, filter, selectedDay, targetFilter, temperatures]);
  const dayGroups = useMemo(() => groupEvidenceByDay(evidence), [evidence]);
  const visibleEvidence = useMemo(() => dayGroups.flatMap((group, index) => {
    const open = openDays === null ? index === 0 : openDays.has(group.day);
    return open ? group.events : [];
  }), [dayGroups, openDays]);
  const paths = useMemo(() => visibleEvidence
    .map((event) => event.payload.evidence_photo?.storage_path)
    .filter((path): path is string => !!path), [visibleEvidence]);
  const pathsKey = paths.join('|');

  const localPreviews = useQuery({
    queryKey: ['operational_evidence_local', profile?.active_tenant_id, pathsKey],
    networkMode: 'always',
    enabled: !!profile?.active_tenant_id && paths.length > 0,
    queryFn: () => listLocalOperationalEvidencePreviews(profile!.active_tenant_id!, paths),
  });
  const links = useQuery<Record<string, PhotoLink>>({
    queryKey: ['operational_evidence_urls', pathsKey],
    enabled: identity.canManageOperationalWork && paths.length > 0 && navigator.onLine,
    staleTime: 4 * 60_000,
    queryFn: async () => {
      const result: Record<string, PhotoLink> = {};
      await Promise.all(paths.map(async (path) => {
        const signed = await getSupabase().storage.from('operational-evidence').createSignedUrl(path, 300);
        result[path] = signed.error
          ? { error: signed.error.message || 'Foto non disponibile sul server' }
          : { url: signed.data?.signedUrl };
      }));
      return result;
    },
  });

  if (!identity.canManageOperationalWork) return <Redirect to="/settings" />;

  const scheduleNames = new Map(schedules.map((row) => [row.id, row.title ?? row.name ?? row.id]));
  const unitNames = new Map(units.map((row) => [row.id, row.name ?? row.title ?? row.id]));
  const personnelNames = new Map(identity.personnel.map((person) => [person.id, person.full_name]));
  const hasPending = Object.keys(localPreviews.data ?? {}).length > 0;

  async function retryPending(): Promise<void> {
    if (!profile?.active_tenant_id || !hasPending) return;
    setRetrying(true);
    try {
      await makeOperationalEvidenceRetryable(profile.active_tenant_id, paths);
      await getSyncEngine().tick();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['events'] }),
        localPreviews.refetch(), links.refetch(),
      ]);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/settings" /></IonButtons>
          <IonTitle>Prove operative</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={filter} onIonChange={(event) => { setFilter((event.detail.value as typeof filter) ?? 'all'); setOpenDays(null); }}>
            <IonSegmentButton value="all"><IonLabel>Tutte</IonLabel></IonSegmentButton>
            <IonSegmentButton value="cleaning"><IonLabel>Pulizie</IonLabel></IonSegmentButton>
            <IonSegmentButton value="temperature"><IonLabel>Temperature</IonLabel></IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 32px', maxWidth: 920, margin: '0 auto' }}>
          <p style={{ margin: '2px 2px 12px', color: palette.textFaint, fontSize: 13, lineHeight: 1.45 }}>
            Foto collegate ai controlli, ordinate nei cassetti della giornata. La data dello scatto resta distinta dall’orario operativo.
          </p>
          <section aria-label="Calendario prove operative" style={{ background: palette.card, border: `1px solid ${palette.border}`, borderRadius: 14, padding: 12, marginBottom: 14 }}>
            <label htmlFor="evidence-day" style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: palette.text, marginBottom: 8 }}>
              <IonIcon icon={calendarOutline} /> Consulta una giornata
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input id="evidence-day" type="date" value={selectedDay} onChange={(event) => { setSelectedDay(event.target.value); setOpenDays(null); }}
                style={{ minHeight: 44, flex: '1 1 190px', padding: '8px 10px', borderRadius: 10, border: `1px solid ${palette.borderStrong}`, background: palette.card, color: palette.text, fontSize: 16 }} />
              {selectedDay && <IonButton fill="outline" size="small" onClick={() => { setSelectedDay(''); setOpenDays(null); }} style={{ minHeight: 44, margin: 0 }}>Tutti i giorni</IonButton>}
            </div>
          </section>

          {hasPending && (
            <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
              <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>Una o più foto sono visibili da questo dispositivo ma attendono ancora il caricamento sicuro.</span>
              <IonButton fill="clear" size="small" disabled={retrying || !navigator.onLine} onClick={() => void retryPending()} aria-label="Riprova caricamento fotografie">
                {retrying ? <IonSpinner name="crescent" /> : <><IonIcon slot="start" icon={refreshOutline} />Riprova</>}
              </IonButton>
            </div>
          )}

          {evidence.length === 0 ? (
            <div style={{ padding: 18, background: palette.card, borderRadius: 14 }}><EmptyHint>Nessuna prova fotografica nel giorno o nel filtro selezionato.</EmptyHint></div>
          ) : dayGroups.map((group, groupIndex) => {
            const isOpen = openDays === null ? groupIndex === 0 : openDays.has(group.day);
            return (
            <details
              key={group.day}
              open={isOpen}
              onToggle={(event) => {
                const nextOpen = event.currentTarget.open;
                setOpenDays((current) => {
                  const next = current === null
                    ? new Set(dayGroups[0] ? [dayGroups[0].day] : [])
                    : new Set(current);
                  if (nextOpen) next.add(group.day); else next.delete(group.day);
                  return next;
                });
              }}
              style={{ background: palette.card, borderRadius: 16, overflow: 'hidden', marginBottom: 12, border: `1px solid ${palette.border}` }}
            >
              <summary style={{ minHeight: 52, boxSizing: 'border-box', cursor: 'pointer', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', color: palette.text }}>
                <IonIcon icon={chevronDownOutline} style={{ fontSize: 18, flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 750, textTransform: 'capitalize' }}>{dayLabel(group.day)}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: palette.textFaint }}>{group.events.length} {group.events.length === 1 ? 'prova' : 'prove'}</span>
              </summary>
              {isOpen && <div style={{ padding: '0 10px 10px' }}>
                {group.events.map((event) => {
                  const photo = event.payload.evidence_photo!;
                  const title = event.kind === 'cleaning_done' ? scheduleNames.get(event.payload.schedule_id ?? '') ?? 'Pulizia' : unitNames.get(event.payload.storage_unit_id ?? '') ?? 'Controllo temperatura';
                  const personId = event.payload._operational?.tenant_personnel_id;
                  const operator = event.payload._operator?.full_name || event.payload.operator_name || (personId ? personnelNames.get(personId) : null) || 'Operatore autenticato';
                  const remote = links.data?.[photo.storage_path];
                  const local = localPreviews.data?.[photo.storage_path];
                  const url = remote?.url ?? local?.dataUrl;
                  const loading = links.isLoading || localPreviews.isLoading;
                  return (
                    <article key={event.id} style={{ background: palette.bg, borderRadius: 13, overflow: 'hidden', marginBottom: 10, border: `1px solid ${palette.border}` }}>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" aria-label={`Apri foto di ${title}`}><img src={url} alt={`Prova di ${title}`} style={{ display: 'block', width: '100%', height: 220, objectFit: 'cover' }} /></a>
                      ) : loading ? (
                        <div style={{ height: 110, display: 'grid', placeItems: 'center', color: palette.textFaint, background: '#e2e8f0' }}><IonSpinner name="crescent" aria-label="Caricamento foto" /></div>
                      ) : (
                        <div role="alert" style={{ minHeight: 110, display: 'grid', placeItems: 'center', textAlign: 'center', padding: 16, color: '#9a3412', background: '#fff7ed', fontSize: 13, lineHeight: 1.4 }}>
                          {navigator.onLine ? 'Foto non ancora disponibile sul server. Se è stata appena acquisita, premi Riprova.' : 'Foto non disponibile su questo dispositivo. Collegati a internet per recuperarla.'}
                        </div>
                      )}
                      <div style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 700, color: palette.text }}>{event.kind === 'cleaning_done' ? '🧽' : '🌡️'} {cancelledCleanings.has(event.id) ? 'Annullata · ' : ''}{title}</div>
                        <div style={{ fontSize: 12.5, color: palette.textFaint, marginTop: 5 }}>
                          {operator} · {new Date(event.client_ts).toLocaleString('it-IT')}
                          {event.kind === 'temperature_reading' && typeof event.payload.temperature_c === 'number' ? ` · ${event.payload.temperature_c.toFixed(1)} °C` : ''}
                        </div>
                        <div style={{ fontSize: 11.5, color: palette.textFaint, marginTop: 3 }}>Foto scattata {new Date(photo.captured_at).toLocaleString('it-IT')}</div>
                        {local && <div style={{ fontSize: 11.5, color: local.lastError ? '#9a3412' : '#9a6700', marginTop: 5 }}>{local.lastError ? 'Caricamento non riuscito · premi Riprova' : 'Salvata sul dispositivo · sincronizzazione in corso'}</div>}
                      </div>
                    </article>
                  );
                })}
              </div>}
            </details>
            );
          })}
          {targetFilter && <IonButton fill="clear" expand="block" routerLink="/settings/operational-evidence">Vedi tutto l’archivio</IonButton>}
        </div>
      </IonContent>
    </IonPage>
  );
}
